import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

declare const gtag: ((...args: any[]) => void) | undefined;

/**
 * Thin wrapper around the GA4 gtag snippet loaded in index.html.
 *
 * Privacy: never pass personal data (email, names, ids, tokens) to GA.
 * Page URLs are sent without query string or fragment because several
 * routes carry emails, invite/confirmation codes or passwords in them.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private started = false;
  private previousLocation = '';

  constructor(private router: Router) {}

  /** Sends a sanitized page_view on every router navigation. Call once at bootstrap. */
  startPageViewTracking(): void {
    if (this.started) return;
    this.started = true;

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const location = AnalyticsService.sanitizeUrl(window.location.href);
        const referrer =
          this.previousLocation || AnalyticsService.sanitizeUrl(document.referrer);

        // `set` so every later event (e.g. sign_up) also uses the sanitized URL.
        this.send('set', {
          page_location: location,
          page_referrer: referrer,
          page_title: document.title,
        });
        this.send('event', 'page_view');
        this.previousLocation = location;
      });
  }

  /** Call only after the backend has confirmed the account was created. */
  trackSignUp(): void {
    this.send('event', 'sign_up', { method: 'email' });
  }

  private send(...args: any[]): void {
    try {
      if (typeof gtag === 'function') {
        gtag(...args);
      }
    } catch {
      // Analytics must never break the app (e.g. blocked by an ad blocker).
    }
  }

  private static sanitizeUrl(url: string): string {
    if (!url) return '';
    try {
      const { origin, pathname } = new URL(url);
      return origin + pathname;
    } catch {
      return '';
    }
  }
}
